import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { usePageContextQuery } from "@/shared/page-context";
import { getUserPreferences } from "./getUserPreferences";
import { mergePayloadIntoPreferences } from "./mergePayloadIntoPreferences";
import { saveSearchPreferences } from "./saveSearchPreferences";
import type { SaveUserPreferencesPayload, SearchPreferencesRead } from "./types";

export const userPreferencesQueryKey = ["user-preferences"];

export const userPreferencesQueryOptions = (useDirectSearchPreferences: boolean) =>
    queryOptions({
        queryKey: userPreferencesQueryKey,
        queryFn: () => getUserPreferences(useDirectSearchPreferences).catch(() => null),
    });

export const useUserPreferencesQuery = () => {
    const { data: pageContext } = usePageContextQuery();
    return useSuspenseQuery(userPreferencesQueryOptions(pageContext.runtimeConfig.useDirectSearchPreferences));
};

export const useSaveUserPreferencesMutation = () => {
    const { data: pageContext } = usePageContextQuery();
    const queryClient = useQueryClient();

    // Preferences require a personal authenticated session; the server remains the authorization boundary.
    const canSavePreferences = pageContext.isAuthenticated && !pageContext.isInstitutional;

    return useMutation({
        mutationFn: (data: SaveUserPreferencesPayload) => {
            if (!canSavePreferences) return Promise.resolve();
            return saveSearchPreferences(
                pageContext.partnerId,
                data,
                pageContext.runtimeConfig.useDirectSearchPreferences,
            );
        },
        onMutate: (data) => {
            if (!canSavePreferences) return;

            // Optimistic update: merge submitted preferences into the cache so that
            // bfcache-restored pages see the freshly-saved values (invalidation is
            // unreliable after bfcache restore since no refetch is triggered).
            const cached = queryClient.getQueryData<SearchPreferencesRead | null>(userPreferencesQueryKey);

            // When cached value is null (institutional user or failed initial fetch),
            // skip the optimistic write — there's no baseline to merge into.
            if (cached == null) return;

            queryClient.setQueryData(userPreferencesQueryKey, mergePayloadIntoPreferences(cached, data));
        },
    });
};
